import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { KeyRound } from 'lucide-react'
import { syncApi } from '@/api/syncApi'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'

/**
 * The person's offline signing keys for the mobile app (z8nrz7fh5c), one per
 * phone. Each phone makes its own key the first time the person proves their
 * password on it, and keeps the key locked with that password; the server
 * holds only the public key. A password change retires every key, and each
 * phone makes a new one at its next online signature.
 */
export function OfflineSigningKeyCard() {
  const qc = useQueryClient()
  const { data: keys, isLoading, error } = useQuery({
    queryKey: ['signing-keys'],
    queryFn: syncApi.signingKeys,
  })
  const retire = useMutation({
    mutationFn: syncApi.retireMySigningKey,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['signing-keys'] }),
  })

  return (
    <Card id='offline-signing-key'>
      <CardHeader>
        <CardTitle className='flex items-center gap-2 text-base'>
          <KeyRound className='h-4 w-4' aria-hidden='true' />
          Offline signing keys
        </CardTitle>
        <CardDescription>
          Let you sign on the mobile app with no connection, one key per phone. You sign with your
          password as usual; the signature is checked when the phone is back online.
        </CardDescription>
      </CardHeader>
      <CardContent className='text-sm'>
        {isLoading ? (
          <p className='text-muted-foreground'>Loading…</p>
        ) : error ? (
          <p className='text-destructive'>Could not load your signing keys.</p>
        ) : keys && keys.length > 0 ? (
          <>
            <ul className='divide-y'>
              {keys.map((k) => (
                <li key={k.id} className='flex items-center justify-between gap-4 py-2'>
                  <div className='min-w-0'>
                    <p className='font-medium'>Phone {k.deviceId.slice(0, 8)}</p>
                    <p className='text-xs text-muted-foreground'>
                      Since {new Date(k.createdAt).toLocaleString()} · key {k.id.slice(0, 8)}
                    </p>
                  </div>
                  <span className='rounded-full bg-emerald-100 px-2 py-0.5 text-xs font-medium text-emerald-800'>
                    Active
                  </span>
                </li>
              ))}
            </ul>
            <Button
              type='button'
              variant='outline'
              size='sm'
              className='mt-3'
              disabled={retire.isPending}
              onClick={() => {
                if (
                  window.confirm(
                    'Retire your offline signing keys? Do this if you lost a phone. Anything a phone signs after this is refused, and each phone makes a new key the next time you sign on it online.',
                  )
                ) {
                  retire.mutate()
                }
              }}
            >
              {retire.isPending ? 'Retiring…' : 'Retire all keys'}
            </Button>
          </>
        ) : (
          <p className='text-muted-foreground'>
            None yet. A phone makes its key the first time you sign in or sign on it online.
          </p>
        )}
      </CardContent>
    </Card>
  )
}
